/**
 * Licht of donker.
 *
 * Drie standen, en de belangrijkste is de eerste: `system` volgt wat de
 * telefoon zelf doet. Daar begint iedereen, en zolang niemand iets kiest hoeft
 * er niets onthouden te worden. Kiest iemand wél, dan blijft die keuze staan —
 * ook als het systeem later omslaat. Dat is precies waarom je hem zou zetten.
 */
export type Theme = 'system' | 'light' | 'dark'

const KEY = 'confetti.theme'

/**
 * Wat er nu geldt. Bij een onbekende waarde in de opslag gaat het terug naar
 * `system`: een kapotte voorkeur is geen reden om de app raar te laten kijken.
 */
export function readTheme(): Theme {
  try {
    const opgeslagen = localStorage.getItem(KEY)

    return opgeslagen === 'light' || opgeslagen === 'dark' ? opgeslagen : 'system'
  } catch {
    // Privémodus in sommige browsers gooit hier. Dan maar de systeemstand.
    return 'system'
  }
}

/**
 * Zet de stand op `<html>`, waar de tokens hem oppikken.
 *
 * Bij `system` gaat het attribuut eraf in plaats van dat er `system` in komt te
 * staan: dan doet de `prefers-color-scheme` in de stylesheet het werk, en dat
 * werkt ook op het moment dat de gebruiker het systeem omzet terwijl de app
 * openstaat, zonder dat hier iets voor hoeft te luisteren.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement

  if (theme === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', theme)
  }

  // De adresbalk van de browser mee. Zonder dit staat er op een donker scherm
  // een crèmekleurige balk bovenaan, en dat valt meer op dan het lijkt.
  const donker =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', donker ? '#17140f' : '#faf4ec')
}

export function saveTheme(theme: Theme): void {
  try {
    if (theme === 'system') {
      localStorage.removeItem(KEY)
    } else {
      localStorage.setItem(KEY, theme)
    }
  } catch {
    // Niet kunnen onthouden is jammer, maar geen reden om het scherm niet om
    // te zetten. De keuze geldt dan voor deze sessie.
  }

  applyTheme(theme)
}
